// src/components/team-messages/ThreadPanel.tsx
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Maximize2, Send, ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { TeamConversation, TeamMessage, TeamMessageAttachment, OrgMember } from '@/types/teamMessages';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatMsgTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDateLabel(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

function isSameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function getConvDisplayName(
  conv: TeamConversation,
  currentUserId: string,
  orgMembers: OrgMember[],
): string {
  if (conv.type === 'channel' || conv.type === 'updates') return `# ${conv.name ?? ''}`;
  if (conv.type === 'group') return conv.name ?? 'Group';
  // DM: name is stamped at load time by getMyConversations(). Fall back to
  // orgMembers lookup and per-member displayName if somehow missing.
  if (conv.name) return conv.name;
  const memberIds = new Set(conv.members.map((m) => m.userId));
  const fromOrg = orgMembers.find((m) => m.userId !== currentUserId && memberIds.has(m.userId));
  if (fromOrg?.displayName) return fromOrg.displayName;
  const other = conv.members.find((m) => m.userId !== currentUserId);
  return other?.displayName || 'Direct Message';
}

// ─── component ──────────────────────────────────────────────────────────────

interface ThreadPanelProps {
  conversation: TeamConversation;
  messages: TeamMessage[];
  loadingMessages: boolean;
  updatesChannelId: string | null;
  currentUserId: string;
  orgMembers: OrgMember[];
  onSend: (body: string, attachments?: TeamMessageAttachment[]) => Promise<void>;
  onBack: () => void;
}

export function ThreadPanel({
  conversation,
  messages,
  loadingMessages,
  updatesChannelId,
  currentUserId,
  orgMembers,
  onSend,
  onBack,
}: ThreadPanelProps) {
  const navigate = useNavigate();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const isReadOnly = conversation.id === updatesChannelId;

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setDraft('');
    try {
      await onSend(body);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#000000]">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#2e2e2e] shrink-0 bg-[#0a0a0a]">
        <button
          onClick={onBack}
          className="p-1 rounded-md hover:bg-[#1e1e1e] transition-colors text-[#5a5a5a] hover:text-white"
          title="Back to conversations"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="flex-1 text-sm font-semibold truncate text-white">
          {getConvDisplayName(conversation, currentUserId, orgMembers)}
        </span>
        <button
          onClick={() => navigate('/team-messages')}
          className="p-1 rounded-md hover:bg-[#1e1e1e] transition-colors text-[#5a5a5a] hover:text-white"
          title="Open full messaging view"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-1">
        {loadingMessages ? (
          <div className="flex items-center justify-center h-full text-sm text-[#8a8a8a]">
            Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-[#8a8a8a]">
            No messages yet
          </div>
        ) : (
          messages.map((msg, idx) => {
            const prevMsg = messages[idx - 1];
            const showDateSep = !prevMsg || !isSameDay(prevMsg.createdAt, msg.createdAt);
            const showSender = !msg.isOwn && (!prevMsg || prevMsg.senderId !== msg.senderId || showDateSep);

            return (
              <div key={msg.id}>
                {showDateSep && (
                  <div className="flex items-center gap-2 my-2">
                    <div className="flex-1 h-px bg-[#2e2e2e]" />
                    <span className="text-[10px] text-[#8a8a8a] tabular-nums shrink-0">
                      {formatDateLabel(msg.createdAt)}
                    </span>
                    <div className="flex-1 h-px bg-[#2e2e2e]" />
                  </div>
                )}

                <MessageBubble msg={msg} showSender={showSender} />
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      {!isReadOnly && (
        <div className="px-3 py-2 border-t border-[#2e2e2e] shrink-0 bg-[#0a0a0a]">
          <div className="flex items-end gap-2 bg-[#1e1e1e] border border-[#2e2e2e] rounded-lg px-3 py-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message… (Enter to send)"
              rows={1}
              className="flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-[#5a5a5a] text-[#fafafa] max-h-24 leading-5"
              style={{ minHeight: '20px' }}
            />
            <button
              onClick={() => void handleSend()}
              disabled={!draft.trim() || sending}
              className="p-1.5 rounded-md bg-white text-black hover:bg-[#e0e0e0] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              title="Send message"
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── sub-components ──────────────────────────────────────────────────────────

function parseJobBody(body: string): { jobRef: string | null; text: string } {
  const match = body.match(/^\[Job\s+([^\]]+)\]\s*/i);
  if (match) return { jobRef: match[1].trim(), text: body.slice(match[0].length).trim() };
  return { jobRef: null, text: body };
}

function MessageBubble({ msg, showSender }: { msg: TeamMessage; showSender: boolean }) {
  const bodyClean = msg.body.replace(/<[^>]*>/g, '').trim();
  const continued = !showSender;

  // ── System message ──────────────────────────────────────────────────────────
  if (msg.isSystem) {
    const meta   = msg.metadata;
    const action = meta?.action ?? null;
    const isSystemUpdate = bodyClean.startsWith('System:');
    const msgText  = isSystemUpdate ? (meta?.title ?? bodyClean) : bodyClean;
    const rawDetail = meta?.detail ? meta.detail.replace(/<[^>]*>/g, '').trim() : null;
    const jobChip = rawDetail
      ? (rawDetail.startsWith('Job ') ? rawDetail.slice(4).trim() : rawDetail)
      : null;
    return (
      <div className={cn('flex items-start gap-2', continued ? 'mt-0.5' : 'mt-2.5')}>
        <div className="w-7 shrink-0 pt-4">
          {!continued ? (
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold text-[#5a5a5a] bg-[#1e1e1e] border border-[#2e2e2e] shrink-0">
              SY
            </div>
          ) : (
            <div className="w-7" />
          )}
        </div>
        <div className="flex flex-col items-start max-w-[75%]">
          {!continued && (
            <span className="text-[11px] text-[#5a5a5a] font-medium mb-0.5 px-1">System</span>
          )}
          <div className={cn(
            'bg-[#141414] border border-[#2e2e2e] px-3 py-2.5',
            continued ? 'rounded-2xl' : 'rounded-2xl rounded-tl-sm',
          )}>
            <p className="text-[#fafafa] text-[12px] font-medium leading-snug">{msgText}</p>
            {jobChip && (
              <span className="inline-flex items-center mt-1.5 rounded border border-[#3a3a3a] bg-[#1e1e1e] px-1.5 py-0.5 text-[10px] font-mono text-[#fafafa] tabular-nums">
                {jobChip}
              </span>
            )}
            {action && (
              <div className="mt-2">
                <Link
                  to={action.path}
                  className="inline-flex items-center gap-1 rounded-md bg-white text-black text-[10px] font-semibold px-2.5 py-1 hover:bg-[#e0e0e0] transition-colors"
                >
                  {action.label}
                  <ArrowRight className="h-2.5 w-2.5" />
                </Link>
              </div>
            )}
          </div>
          <span className="text-[10px] text-[#5a5a5a] tabular-nums mt-0.5 px-1">
            {formatMsgTime(msg.createdAt)}
          </span>
        </div>
      </div>
    );
  }

  const { jobRef, text: msgText } = parseJobBody(bodyClean);
  const action = msg.metadata?.action ?? null;

  if (msg.isOwn) {
    return (
      <div className={cn('flex justify-end', continued ? 'mt-0.5' : 'mt-2.5')}>
        <div className="flex flex-col items-end max-w-[75%]">
          <div
            className={cn(
              'bg-white text-black px-3.5 py-2 text-[13px] leading-[1.5] whitespace-pre-wrap',
              continued ? 'rounded-2xl' : 'rounded-2xl rounded-tr-sm',
            )}
          >
            {msgText}
            {jobRef && (
              <div className="mt-1.5">
                <span className="inline-flex items-center rounded border border-black/15 bg-black/5 px-1.5 py-0.5 text-[10px] font-mono text-black tabular-nums">
                  {jobRef}
                </span>
              </div>
            )}
            {action && (
              <div className="mt-1.5 pt-1.5 border-t border-black/10">
                <Link
                  to={action.path}
                  className="inline-flex items-center gap-1 rounded-md bg-black text-white text-[10px] font-semibold px-2.5 py-1 hover:bg-black/80 transition-colors"
                >
                  {action.label}
                  <ArrowRight className="h-2.5 w-2.5" />
                </Link>
              </div>
            )}
          </div>
          <span className="text-[10px] text-[#5a5a5a] tabular-nums mt-0.5 px-1">
            {formatMsgTime(msg.createdAt)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex items-start gap-2', continued ? 'mt-0.5' : 'mt-2.5')}>
      <div className="w-7 shrink-0 pt-4">
        {!continued ? (
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-[#2e2e2e] shrink-0">
            {msg.senderInitials || msg.senderName?.slice(0, 2) || '??'}
          </div>
        ) : (
          <div className="w-7" />
        )}
      </div>
      <div className="flex flex-col items-start max-w-[75%]">
        {!continued && (
          <span className="text-[11px] text-[#5a5a5a] font-medium mb-0.5 px-1">
            {msg.senderName}
          </span>
        )}
        <div
          className={cn(
            'bg-[#1a1a1a] text-[#fafafa] border border-[#2e2e2e] px-3.5 py-2 text-[13px] leading-[1.5] whitespace-pre-wrap',
            continued ? 'rounded-2xl' : 'rounded-2xl rounded-tl-sm',
          )}
        >
          {msgText}
          {jobRef && (
            <div className="mt-1.5">
              <span className="inline-flex items-center rounded border border-[#3a3a3a] bg-[#1e1e1e] px-1.5 py-0.5 text-[10px] font-mono text-[#fafafa] tabular-nums">
                {jobRef}
              </span>
            </div>
          )}
          {action && (
            <div className="mt-1.5 pt-1.5 border-t border-[#2E2E2E]">
              <Link
                to={action.path}
                className="inline-flex items-center gap-1 rounded-md bg-white text-black text-[10px] font-semibold px-2.5 py-1 hover:bg-[#e0e0e0] transition-colors"
              >
                {action.label}
                <ArrowRight className="h-2.5 w-2.5" />
              </Link>
            </div>
          )}
        </div>
        <span className="text-[10px] text-[#5a5a5a] tabular-nums mt-0.5 px-1">
          {formatMsgTime(msg.createdAt)}
        </span>
      </div>
    </div>
  );
}

